export interface VdbUploadUrlPrefetchConfig {
  apiBaseUrl: string;
  bearerToken: string;
}

export interface PrefetchedVdbUploadUrl {
  uploadUrl: string;
  vdbGcsUrl: string;
  objectName: string;
}

interface CachedPrefetch {
  key: string;
  expiresAt: number;
  value: PrefetchedVdbUploadUrl;
}

interface InFlightPrefetch {
  key: string;
  promise: Promise<boolean>;
}

const PREFETCH_TTL_MS = 10 * 60 * 1000;

const resolveBearerTokenCacheScope = (bearerToken: string): string => {
  try {
    const payloadSegment = bearerToken.split(".")[1];
    if (!payloadSegment || typeof globalThis.atob !== "function") {
      return `token:${bearerToken}`;
    }

    const normalizedPayload = payloadSegment
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payloadSegment.length / 4) * 4, "=");
    const payload = JSON.parse(globalThis.atob(normalizedPayload)) as Record<
      string,
      unknown
    >;
    const subject = typeof payload.sub === "string" ? payload.sub.trim() : "";
    const issuer = typeof payload.iss === "string" ? payload.iss.trim() : "";
    if (subject) {
      return `identity:${issuer}\n${subject}`;
    }
  } catch {
    // JWT가 아닌 토큰은 기존처럼 토큰 자체로 cache scope를 분리한다.
  }

  return `token:${bearerToken}`;
};

const normalizeConfig = (
  config: VdbUploadUrlPrefetchConfig,
): VdbUploadUrlPrefetchConfig | null => {
  const apiBaseUrl = config.apiBaseUrl.trim().replace(/\/+$/, "");
  const bearerToken = config.bearerToken.trim();
  if (!apiBaseUrl || !bearerToken) return null;
  return { apiBaseUrl, bearerToken };
};

const buildConfigKey = (config: VdbUploadUrlPrefetchConfig): string =>
  `${config.apiBaseUrl}\n${resolveBearerTokenCacheScope(config.bearerToken)}`;

const isSupportedStorageUri = (value: string): boolean =>
  /^s3:\/\//i.test(value) || /^gs:\/\//i.test(value);

export class EditorVdbUploadUrlPrefetchPool {
  private cached: CachedPrefetch | null = null;
  private inFlight: InFlightPrefetch | null = null;
  private generation = 0;

  constructor(
    private readonly fetchImpl: typeof fetch = globalThis.fetch.bind(
      globalThis,
    ),
    private readonly now: () => number = Date.now,
  ) {}

  clear(): void {
    this.generation += 1;
    this.cached = null;
    this.inFlight = null;
  }

  take(rawConfig: VdbUploadUrlPrefetchConfig): PrefetchedVdbUploadUrl | null {
    const config = normalizeConfig(rawConfig);
    if (!config) return null;

    const key = buildConfigKey(config);
    if (this.cached && this.cached.key !== key) {
      this.clear();
      return null;
    }
    if (!this.cached) return null;
    if (this.cached.expiresAt <= this.now()) {
      this.cached = null;
      return null;
    }

    const value = this.cached.value;
    this.cached = null;
    return value;
  }

  async prefetch(rawConfig: VdbUploadUrlPrefetchConfig): Promise<boolean> {
    const config = normalizeConfig(rawConfig);
    if (!config) return false;

    const key = buildConfigKey(config);
    if (
      (this.cached && this.cached.key !== key) ||
      (this.inFlight && this.inFlight.key !== key)
    ) {
      this.clear();
    }

    if (this.cached?.key === key && this.cached.expiresAt > this.now()) {
      return true;
    }

    if (this.inFlight?.key === key) {
      return this.inFlight.promise;
    }

    const generation = this.generation;
    const promise = this.fetchAndCache(config, key, generation);
    this.inFlight = { key, promise };
    try {
      return await promise;
    } finally {
      if (this.inFlight?.promise === promise) {
        this.inFlight = null;
      }
    }
  }

  private async fetchAndCache(
    config: VdbUploadUrlPrefetchConfig,
    key: string,
    generation: number,
  ): Promise<boolean> {
    try {
      const endpoint = new URL(
        `${config.apiBaseUrl}/conversion/viewr-vdb-upload-url`,
      );
      endpoint.searchParams.set("content_type", "application/octet-stream");
      endpoint.searchParams.set("_", String(this.now()));

      const response = await this.fetchImpl(endpoint.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${config.bearerToken}`,
        },
        cache: "no-store",
      });
      if (!response.ok) return false;

      const payload = (await response.json()) as Record<string, unknown>;
      const uploadUrl =
        typeof payload.upload_url === "string" ? payload.upload_url.trim() : "";
      const vdbGcsUrl =
        typeof payload.vdb_gcs_url === "string"
          ? payload.vdb_gcs_url.trim()
          : "";
      const objectName =
        typeof payload.object_name === "string"
          ? payload.object_name.trim()
          : "";

      let parsedUploadUrl: URL;
      try {
        parsedUploadUrl = new URL(uploadUrl);
      } catch {
        return false;
      }
      if (
        parsedUploadUrl.protocol !== "https:" ||
        !isSupportedStorageUri(vdbGcsUrl)
      ) {
        return false;
      }

      if (this.generation !== generation) return false;
      this.cached = {
        key,
        expiresAt: this.now() + PREFETCH_TTL_MS,
        value: { uploadUrl, vdbGcsUrl, objectName },
      };
      return true;
    } catch {
      return false;
    }
  }
}

export const editorVdbUploadUrlPrefetchPool =
  new EditorVdbUploadUrlPrefetchPool();
